import { Mic, MicOff, Send, Square } from 'lucide-react';
import { Button, Textarea, cn } from '@mlm/ui';
import { useSpeechInput } from '../../lib/use-speech';

/**
 * Message composer: textarea + send + mic (dictation APPENDS the transcript
 * for review — never auto-sends) + the remaining-today counter when the tenant
 * caps daily messages. While an answer streams, Send becomes Stop.
 */
export function Composer({
  draft,
  onDraftChange,
  onSubmit,
  onStop,
  sending,
  capRemaining,
  creditsLeft = null,
  autoRead = false,
  onAutoReadChange,
}: {
  draft: string;
  onDraftChange: (v: string) => void;
  onSubmit: () => void;
  /** Present while a streamed answer is in flight. */
  onStop?: () => void;
  sending: boolean;
  capRemaining: number | null;
  /** Credits left (free allowance + wallet); null = not metered by credits. */
  creditsLeft?: number | null;
  /** Read streamed replies aloud as they arrive (hidden when TTS unsupported). */
  autoRead?: boolean;
  onAutoReadChange?: (v: boolean) => void;
}) {
  const speech = useSpeechInput((text) =>
    onDraftChange(draft ? `${draft.replace(/\s+$/, '')} ${text}` : text),
  );
  const capBlocked = capRemaining !== null && capRemaining <= 0;

  return (
    <div className="border-t pt-3">
      <div className="flex items-end gap-2">
        <Textarea
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSubmit();
            }
          }}
          placeholder={capBlocked ? 'Daily limit reached — try again tomorrow' : 'Ask me anything…'}
          rows={1}
          maxLength={4000}
          disabled={sending || capBlocked}
          className="max-h-32 min-h-[2.5rem] flex-1 resize-none"
        />
        {speech.supported && (
          <Button
            type="button"
            variant={speech.listening ? 'destructive' : 'outline'}
            className={cn('flex-none', speech.listening && 'animate-pulse')}
            aria-label={speech.listening ? 'Stop dictation' : 'Dictate a message'}
            disabled={sending || capBlocked}
            onClick={() => (speech.listening ? speech.stop() : speech.start())}
          >
            {speech.listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </Button>
        )}
        {sending && onStop ? (
          <Button
            type="button"
            variant="outline"
            onClick={onStop}
            className="flex-none"
            aria-label="Stop generating"
          >
            <Square className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            onClick={onSubmit}
            disabled={!draft.trim() || sending || capBlocked}
            className="flex-none"
            aria-label="Send"
          >
            <Send className="h-4 w-4" />
          </Button>
        )}
      </div>
      {(capRemaining !== null || creditsLeft !== null || onAutoReadChange) && (
        <div className="mt-1 flex items-center justify-between gap-2">
          {onAutoReadChange ? (
            <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 cursor-pointer accent-primary"
                checked={autoRead}
                onChange={(e) => onAutoReadChange(e.target.checked)}
              />
              <span>Read replies aloud</span>
            </label>
          ) : (
            <span />
          )}
          <p className="text-right text-[11px] text-muted-foreground">
            {creditsLeft !== null && (
              <span>
                {creditsLeft > 0
                  ? `${creditsLeft} ${creditsLeft === 1 ? 'answer' : 'answers'} left`
                  : 'No answers left'}
              </span>
            )}
            {creditsLeft !== null && capRemaining !== null && <span> · </span>}
            {capRemaining !== null && (
              <span>
                {capRemaining > 0
                  ? `${capRemaining} message${capRemaining === 1 ? '' : 's'} left today`
                  : 'Daily limit reached'}
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
